import React, { useState, useEffect } from "react";
import { apiList } from "../../../helpers/apiList";
import CustomAlert from "../../../helpers/CustomAlert";
import { axiosPostApi } from "../../../helpers/interceptorServices";
import AddVectorIcon from "../../../assets/Icons/Add Vector.png";
import CrossIcon from "../../../assets/Icons/cross_svg.svg";
import LeftCircle from "../../../assets/Icons/Left circle B.png";
import RightCircle from "../../../assets/Icons/Right circle A.png";
import Dropdown from "../../../components/Dropdown";
import ModalInput from "../../../components/ModalInput/ModalInput";
import { MapPin } from "lucide-react";
import Loader from "../../../components/Loader/Loader";

const GetZoneModal = ({
  show,
  handleClose,
  editData,
  action,
}) => {
  const loggedInUserName = sessionStorage.getItem("username");
  const [formData, setFormData] = useState({
    sourcePincode: "",
    destinationPincode: "",
  });
  const [errors, setErrors] = useState({});
  const [zoneData, setZoneData] = useState("");
  const [loading, setLoading] = useState(false);
  const isReadOnly = action === "view";

  useEffect(() => {
    if (action === "add") {
      setFormData({
        sourcePincode: "",
        destinationPincode: "",
      });
    }
    setErrors({});
  }, [show, action]);

  const validate = () => {
    const newErrors = {};
    if (!formData.sourcePincode.trim()) {
      newErrors.sourcePincode = "Pincode is required";
    }else if (!/^\d{6}$/.test(formData.sourcePincode.trim())) {
        newErrors.sourcePincode = "Pincode must be a 6-digit number";
    }

    if (!formData.destinationPincode.trim()) {
      newErrors.destinationPincode = "Pincode is required";
    }else if (!/^\d{6}$/.test(formData.destinationPincode.trim())) {
        newErrors.destinationPincode = "Pincode must be a 6-digit number";
    }
    return newErrors;
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
        fetchGetZoneData();
    }
  };

  const fetchGetZoneData = async () => {
    const payload = {
        username: loggedInUserName,
        sourcePincode: formData.sourcePincode.trim(),
        destinationPincode: formData.destinationPincode.trim(),
    }
    setLoading(true);
    try {
        const response = await axiosPostApi(apiList.FETCH_GET_ZONE, payload);
        if (response?.status === 200 && response?.data?.status === true) {
            const data = response?.data?.data || "N/A";
            setZoneData(data);
            setLoading(false);
        } else {
            CustomAlert({
                title: "Failed",
                text: response.data.message,
                icon: "error",
                confirmButtonText: "Retry",
            });
        }
    } catch (error) {
        CustomAlert({
            title: "Error",
            text: "An error occurred while fetching zone information.",
            icon: "error",
            confirmButtonText: "Retry",
        });
    } finally{
        setLoading(false);
    }
  }

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    if (!/^\d*$/.test(value)) return;
    setFormData((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => ({ ...prev, [id]: "" }));
  }

  const handleBlur = (e) => {
    const { id, value } = e.target;
    let error = "";
    if (!value.trim()) {
        error = id === "sourcePincode" ? "Pincode is required" : "Pincode is required";
    } else if (!/^\d{6}$/.test(value.trim())) {
        error = id === "sourcePincode" ? "Pincode must be a 6-digit number" : "Pincode must be a 6-digit number";
    }
    setErrors((prev) => ({ ...prev, [id]: error }));
}

const handleCloseBtn = () => {
    setFormData({
        sourcePincode: "",
        destinationPincode: "",
    });
    setErrors({});
    setZoneData("");
    handleClose();
}

  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: "rgba(0,0,0,0.4)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1031,
      }}
    >
      <div className="bg-white w-[520px] rounded-[20px] p-6 shadow-lg relative border border-gray-200">
        {/* Header */}
        {/* Decorative Circles (background layer) */}
        <img
          src={LeftCircle}
          alt="leftImg"
          className="absolute left-0 bottom-0 w-[110px] h-[105px] z-0"
        />
        <img
          src={RightCircle}
          alt="rightImg"
          className="absolute right-0 top-0 w-[110px] h-[105px] z-0"
        />
        {/* Header Content (foreground layer) */}
        <div
          className="flex justify-between items-center mb-3 relative z-10 pb-3"
          style={{
            borderBottom: "1px solid #dee2e6",
          }}
        >
          <div className="flex items-center gap-2">
            <img src={AddVectorIcon} alt="Add" className="w-7 h-7" />
            <h2 className="text-lg font-semibold text-[#FF6600]">
              Search Zone
            </h2>
          </div>
          {/* Close Icon */}
          <img
            src={CrossIcon}
            alt="Close"
            onClick={handleClose}
            className="w-5 h-5 cursor-pointer hover:rotate-90 transition-transform z-20"
          />
        </div>
        {/* Form Fields */}
        <div className="grid grid-cols-2 gap-4">
            {/* Source Pincode */}
            <ModalInput
              id="sourcePincode"
              label="Source Pincode"
              value={formData.sourcePincode}
              onChange={handleInputChange}
              onBlur={handleBlur}
              error={errors.sourcePincode}
              placeholder="Enter source pincode"
              disabled={isReadOnly}
              Icon={MapPin}
              maxLength={6}
            />
        
            {/* Destination Pincode */}
            <ModalInput
              id="destinationPincode"
              label="Destination Pincode"
              value={formData.destinationPincode}
              onChange={handleInputChange}
              onBlur={handleBlur}
              error={errors.destinationPincode}
              placeholder="Enter destination pincode"
              disabled={isReadOnly}
              Icon={MapPin}
              maxLength={6}
            />
            <div className="col-span-2 mt-2 p-3 border rounded-lg bg-gray-50">
              <div>
                <span className="text-sm text-gray-700">Zone: </span>
                <span className="text-sm font-medium text-[#FF6600]">{zoneData?.zone || "N/A"}</span>
              </div>
              <div className="flex justify-between mt-1">
              <div>
                <span className="text-sm text-gray-700">Source City: </span>
                <span className="text-sm font-medium text-[#FF6600]">{zoneData?.source?.city || "N/A"}</span>
              </div>
               <div>
                <span className="text-sm text-gray-700">Destination City: </span>
                <span className="text-sm font-medium text-[#FF6600]">{zoneData?.destination?.city || "N/A"}</span>
              </div>
              </div>                
            </div>
        </div>

        {/* Footer */}
        <div className="flex justify-center gap-4 mt-6">
          <button
            onClick={handleCloseBtn}
            className="btn btn-back !rounded-[8px] w-[133px] h[33px]"
          >
            Cancel
          </button>
            <button
              onClick={handleSubmit}
              className="btn btn-submit !rounded-[8px] w-[133px] h[33px]"
              disabled={loading}
            >
                {loading ? (
                    <Loader />
                ) : "Check"}
            </button>
        </div>
      </div>
    </div>
  );
};

export default GetZoneModal;
